import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { ApiErrorCode } from "../enums/api-error-code.enum";
import { ISuspensionNotice } from "../interfaces/suspension-notice.interface";

const BASEURL = import.meta.env.VITE_API_ENDPOINT;
const ORG_BASEURL = import.meta.env.VITE_ORG_API_ENDPOINT;
// Scheme for tenant-subdomain API calls. VITE_ORG_API_ENDPOINT is a bare host
// (no scheme), so the org base URL needs a protocol. Defaults to https; set
// VITE_BACKEND_PROTOCOL=http only if your local API is served over plain http.
const ORG_PROTOCOL = import.meta.env.VITE_BACKEND_PROTOCOL ?? "https";

const controllers: any = {};

const httpClient = axios.create({
    baseURL: BASEURL,
});

let initPromise: Promise<any> | null = null;

// Slug the /custom-domains map resolved for THIS hostname, if any. Kept
// separately from localStorage["org"], which other flows also write, so callers
// can tell an authoritative custom-domain mapping from a leftover value.
let customDomainOrg: string | null = null;

export const getCustomDomainOrg = (): string | null => customDomainOrg;

export const initializeCustomDomain = async (): Promise<any> => {
    if (!initPromise) {
        initPromise = (async () => {
            try {
                const { data } = await axios.get(
                    BASEURL + "/api/v1/custom-domains"
                );
                if (data.data.hasOwnProperty(window.location.hostname)) {
                    customDomainOrg = data.data[window.location.hostname];
                    localStorage.setItem(
                        "org",
                        data.data[window.location.hostname]
                    );
                }
            } catch (err) {
                console.error("Init error:", err);
                initPromise = null; // So it can retry on failure
                // Do not throw: allow requests to continue with existing org (e.g. from 2FA).
                // Safari can fail this request (CORS/ITP), which would otherwise break login flow.
            }
        })();
    }

    return initPromise;
};
httpClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const url = config.url;
        const token = JSON.parse(
            localStorage.getItem("token") || "{}"
        )?.access_token;

        let org = localStorage.getItem("org");
        // Only block on custom-domains when org is missing (e.g. first visit).
        // When org exists (e.g. set by 2FA), skip so Safari CORS/ITP cannot break the profile request.
        if (!org) {
            await initializeCustomDomain();
            org = localStorage.getItem("org");
        }

        if (controllers[url as any]) {
            controllers[url as any].abort();
        }

        if (org) {
            config.baseURL = `${ORG_PROTOCOL}://${org}.${ORG_BASEURL}`;

            if (config.url?.includes("/auth/token") && !config.data["2fa"]) {
                config.baseURL = BASEURL;
            }
        }
        config.headers["Authorization"] = token ? `Bearer ${token}` : "";

        const controller = new AbortController();
        config.signal = controller.signal;
        controllers[url as any] = controller;

        return config;
    }
);

httpClient.interceptors.response.use(
    (response: AxiosResponse<any>) => {
        // Capture the impersonation session marker before the unwrap below
        // drops `response.headers`. Axios lowercases header keys. The flag is
        // only cleared on login + logout — never on an absent header — so the
        // brief pre-login window without the header can't race it away. NOTE:
        // the backend sends this header for ANY session of the impersonated
        // user/org (including the real user's own), so this marker alone does
        // NOT mean "this tab is the impersonation tab" — that requires the
        // per-tab "impersonation_boot" sessionStorage flag set by the ?ic=
        // handoff (see useImpersonationBoot / useImpersonationSession).
        const impersonationSession = response.headers["x-impersonation-session"];
        if (impersonationSession) {
            localStorage.setItem("impersonation_session_id", impersonationSession);
        }
        return response.data;
    },
    (error) => {
        const url = error.config?.url;
        if (url) delete controllers[url];

        // A suspended tenant 403s every request. Hand the backend-supplied copy
        // to the suspension gate and stop here: no toast, no Sentry, and a
        // never-settling promise so downstream .catch() handlers cannot fire
        // their own error UI either (same device as the CanceledError branch).
        const suspensionErrors = error?.response?.data?.errors;

        if (suspensionErrors?.code === ApiErrorCode.OrganizationSuspended) {
            window.dispatchEvent(
                new CustomEvent<ISuspensionNotice>("organization:suspended", {
                    detail: {
                        isSuspended: true,
                        title: suspensionErrors?.title ?? null,
                        message: suspensionErrors?.message ?? null,
                    },
                })
            );

            return new Promise(() => {});
        }

        if (error.name === "CanceledError" ||  url?.endsWith("/api/v1/analytics")) {
            return new Promise(() => {});
        }
        if (!error?.response || error?.response?.status !== 422) {
            const SENTRY_ENABLED =
                import.meta.env.VITE_SENTRY_ENABLED !== "false";
            if (SENTRY_ENABLED) {
                try {
                    Sentry.captureException(error);
                } catch (e) {
                    // do nothing
                }
            }
        }

        let errorMessage =
            error?.response?.data?.errors?.messages ??
            "Failed to complete your request.";
        let messages = "";

        if (
            error?.response?.data?.errors &&
            error?.response?.data?.errors?.messages &&
            typeof error?.response?.data?.errors?.messages === "object" &&
            !Array.isArray(error?.response?.data?.errors?.messages)
        ) {
            for (const key in error?.response?.data?.errors?.messages) {
                if (
                    Array.isArray(error?.response?.data?.errors?.messages[key])
                ) {
                    messages +=
                        error?.response?.data?.errors?.messages[key].join("\n");
                }
            }
        } else if (
            error?.response?.data?.errors &&
            error?.response?.data?.errors.messages &&
            Array.isArray(error?.response?.data?.errors?.messages)
        ) {
            messages += error?.response?.data?.errors?.messages?.join("\n");
        } else if (
            error?.response?.data?.errors &&
            error?.response?.data?.errors?.messages &&
            typeof error?.response?.data?.errors?.messages === "string"
        ) {
            messages = error?.response?.data?.errors?.messages;
        } else {
            try {
                Object.keys(error?.response?.data?.errors).forEach((key) => {
                    toast.error(error?.response?.data?.errors[key].join("\n"));
                });
            } catch (e) {
                messages = errorMessage;
            }
        }

        if (messages === "Invalid Token") {
            messages =
                "Your invitation has expired, please contact the support.";
        }

        if (messages.trim().length) {
            toast.error(messages);
        }

        throw error;
    }
);

export default httpClient;
